import { Slot } from '@radix-ui/react-slot'
import { ChevronRightIcon, HomeIcon, MoreHorizontal } from 'lucide-react'
import Link from 'next/link'
import type { ComponentProps } from 'react'
import * as React from 'react'

import { cn } from '~/lib/utils'

const Breadcrumb = React.forwardRef<
	HTMLElement,
	React.ComponentPropsWithoutRef<'nav'> & {
		separator?: React.ReactNode
	}
>(({ ...props }, ref) => (
	<nav
		ref={ref}
		aria-label='breadcrumb'
		{...props}
	/>
))

Breadcrumb.displayName = 'Breadcrumb'

const BreadcrumbList = React.forwardRef<
	HTMLOListElement,
	React.ComponentPropsWithoutRef<'ol'>
>(({ className, ...props }, ref) => (
	<ol
		ref={ref}
		className={cn(
			'flex flex-wrap items-center gap-1.5 break-words font-semibold text-sm sm:gap-2.5',
			className,
		)}
		{...props}
	/>
))

BreadcrumbList.displayName = 'BreadcrumbList'

const BreadcrumbItem = React.forwardRef<
	HTMLLIElement,
	React.ComponentPropsWithoutRef<'li'>
>(({ className, ...props }, ref) => (
	<li
		ref={ref}
		className={cn('inline-flex h-[32px] items-center gap-1.5', className)}
		{...props}
	/>
))

BreadcrumbItem.displayName = 'BreadcrumbItem'

const BreadcrumbLink = React.forwardRef<
	HTMLAnchorElement,
	React.ComponentPropsWithoutRef<typeof Link> & {
		asChild?: boolean
	}
>(({ asChild, className, ...props }, ref) => {
	const Comp = asChild ? Slot : Link

	return (
		<Comp
			ref={ref}
			className={cn(
				'flex h-full flex-row items-center justify-center rounded bg-card px-4 transition-colors hover:text-primary',
				className,
			)}
			{...props}
		/>
	)
})

BreadcrumbLink.displayName = 'BreadcrumbLink'

const BreadcrumbPage = React.forwardRef<
	HTMLSpanElement,
	React.ComponentPropsWithoutRef<'span'>
>(({ className, ...props }, ref) => (
	<span
		ref={ref}
		role='link'
		aria-disabled='true'
		aria-current='page'
		className={cn(
			'flex h-full flex-row items-center justify-center rounded bg-card px-4 text-foreground',
			className,
		)}
		{...props}
	/>
))

BreadcrumbPage.displayName = 'BreadcrumbPage'

const BreadcrumbSeparator = ({
	children,
	className,
	...props
}: React.ComponentProps<'li'>) => (
	<li
		role='presentation'
		aria-hidden='true'
		className={cn('[&>svg]:size-3.5', className)}
		{...props}
	>
		{children || <ChevronRightIcon />}
	</li>
)

BreadcrumbSeparator.displayName = 'BreadcrumbSeparator'

const BreadcrumbEllipsis = ({
	className,
	...props
}: React.ComponentProps<'span'>) => (
	<span
		role='presentation'
		aria-hidden='true'
		className={cn(
			'flex h-full w-9 flex-row items-center justify-center rounded bg-card',
			className,
		)}
		{...props}
	>
		<MoreHorizontal className='size-4' />

		<span className='sr-only'>More</span>
	</span>
)

BreadcrumbEllipsis.displayName = 'BreadcrumbElipssis'

const HomeBreadcrumbItem = ({
	className,
	...props
}: Omit<ComponentProps<typeof BreadcrumbLink>, 'href'>) => (
	<BreadcrumbItem>
		<BreadcrumbLink
			{...props}
			href='/dashboard'
			className={cn('w-[32px] px-0', className)}
		>
			<HomeIcon size={14} />
		</BreadcrumbLink>
	</BreadcrumbItem>
)

HomeBreadcrumbItem.displayName = 'HomeBreadcrumbItem'

export {
	Breadcrumb,
	BreadcrumbEllipsis,
	BreadcrumbItem,
	BreadcrumbLink,
	BreadcrumbList,
	BreadcrumbPage,
	BreadcrumbSeparator,
	HomeBreadcrumbItem,
}
